// =====================================================================
// Faz AK (2026-05-07) — WhatsApp Mesaj Paneli
// =====================================================================
// Owner test mesajı gönderir, log'u görür, provider durumu kontrol eder.
//
// Provider: backend WHATSAPP_PROVIDER env'ine göre
//   - test: gerçek gönderim yok, sadece DB log (default)
//   - twilio: Twilio WhatsApp Business API
//   - meta: Meta WhatsApp Cloud API
//
// Endpoint:
//   GET  /api/admin/whatsapp/status
//   GET  /api/admin/whatsapp/log
//   POST /api/admin/whatsapp/send
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button, TextField
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// Status → renk + Türkçe etiket
const STATUS_META = {
  queued:    { color: "#94a3b8", label: "Sırada" },
  sent:      { color: "#3b82f6", label: "Gönderildi" },
  delivered: { color: "#10b981", label: "İletildi" },
  read:      { color: "#10b981", label: "Okundu" },
  failed:    { color: "#ef4444", label: "Başarısız" }
};

export default function WhatsAppPanel() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Send form state
  const [toPhone, setToPhone] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusR, logR] = await Promise.allSettled([
        API.get("/api/admin/whatsapp/status"),
        API.get("/api/admin/whatsapp/log?limit=30")
      ]);
      setStatus(statusR.status === "fulfilled" ? statusR.value.data : null);
      setLog(logR.status === "fulfilled" ? (logR.value.data.items || []) : []);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };
  // async load → cleanup'ta Promise çağrılmasın (arrow wrap)
  useEffect(() => { load(); }, []);

  const submitSend = async () => {
    if (!toPhone || !body) { toast.error("Telefon + mesaj zorunlu"); return; }
    setSending(true);
    try {
      const r = await API.post("/api/admin/whatsapp/send", {
        to_phone: toPhone, body
      });
      if (r.data?.success) {
        toast.success(r.data.mode === "test"
          ? `✓ Test modunda log alındı (#${r.data.log_id})`
          : `✓ Gönderildi (ID: ${r.data.external_id || r.data.log_id})`);
        setBody("");  // Sadece body sıfırla, telefon aynı kalsın test için
        load();
      } else {
        toast.error(r.data?.error || "Bilinmeyen hata");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading && !status) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;

  const stats = status?.stats_30d || {};
  const isTestMode = status?.provider === "test";

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>WhatsApp Mesajları</Typography>
          <Typography variant="body2" color="text.secondary">
            Müşterilere WhatsApp Business üzerinden bildirim
          </Typography>
        </Box>
        <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Provider durum kartı */}
      <Card sx={{
        p: 2.5, borderRadius: 3, mb: 3,
        bgcolor: isTestMode ? "#fff7ed" : "#f0fdf4",
        border: isTestMode ? "2px solid #f97316" : "2px solid #10b981"
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <WhatsAppIcon sx={{ color: isTestMode ? "#f97316" : "#10b981", fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {isTestMode ? "⚠️ TEST MODE" : `✅ Aktif: ${status?.provider?.toUpperCase()}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isTestMode
                ? "Şu an gerçek mesaj gönderilmiyor — sadece DB'ye log alınıyor."
                : `Gönderici: ${status?.from_phone || "(belirsiz)"} · Aktif provider: ${status?.provider}`}
            </Typography>
          </Box>
        </Box>
        {isTestMode && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "white", borderRadius: 1.5, fontFamily: "monospace", fontSize: 11 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 700 }}>
              Aktivasyon (.env'e ekle, docker restart):
            </Typography>
            <Box sx={{ color: "#1565C0" }}>
              # Twilio (sandbox veya production):<br/>
              WHATSAPP_PROVIDER=twilio<br/>
              WHATSAPP_FROM=+14155238886<br/>
              TWILIO_ACCOUNT_SID=ACxxxxx<br/>
              TWILIO_AUTH_TOKEN=xxxxx<br/>
              <br/>
              # veya Meta WhatsApp Cloud API (1000 msg/ay free):<br/>
              WHATSAPP_PROVIDER=meta<br/>
              META_WA_PHONE_ID=xxxxx<br/>
              META_WA_TOKEN=xxxxx
            </Box>
          </Box>
        )}
      </Card>

      {/* 5 KPI: 30g istatistik */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam (30g)</Typography>
            <Typography variant="h4" fontWeight={800}>{Number(stats.total) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Gönderildi</Typography>
            <Typography variant="h4" fontWeight={800} color="#3b82f6">{Number(stats.sent) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">İletildi</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{Number(stats.delivered) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Okundu</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{Number(stats.readCount) || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Başarısız</Typography>
            <Typography variant="h4" fontWeight={800} color={stats.failed > 0 ? "#ef4444" : "#94a3b8"}>
              {Number(stats.failed) || 0}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Test gönderim formu */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Manuel Mesaj Gönder</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth label="Telefon" value={toPhone}
              onChange={e => setToPhone(e.target.value)}
              placeholder="+905551234567 veya 05551234567"
              helperText="Türkiye numaraları otomatik +90'lanır"
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth label="Mesaj" value={body}
              onChange={e => setBody(e.target.value)}
              multiline rows={2}
              placeholder="Sipariş #42 onaylandı, takip linki: https://api.miksanplastik.com.tr/api/track/XXXX"
              helperText={`${body.length} karakter`}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained" startIcon={<SendIcon />}
              onClick={submitSend} disabled={sending || !toPhone || !body}
              sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#128C7E" } }}
            >
              {sending ? "Gönderiliyor..." : isTestMode ? "Test Gönder (DB log)" : "Gönder"}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Mesaj log tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>Son Mesaj Log'u</Typography>
        </Box>
        {log.length === 0 ? (
          <Box sx={{ p: 3 }}><Alert severity="info">Henüz mesaj yok</Alert></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Zaman</strong></TableCell>
                <TableCell><strong>Telefon</strong></TableCell>
                <TableCell><strong>Mesaj</strong></TableCell>
                <TableCell align="center"><strong>Durum</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {log.map(item => {
                const sm = STATUS_META[item.status] || STATUS_META.queued;
                return (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontSize: 11 }}>{fmtDate(item.sent_at)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{item.to_phone}</TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.body}
                      {item.error_message && (
                        <Typography variant="caption" color="#ef4444" sx={{ display: "block", fontSize: 10 }}>
                          ⚠️ {item.error_message}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sm.label} size="small" sx={{ bgcolor: sm.color, color: "white", fontWeight: 700, fontSize: 11 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
